import React, { useEffect, useState } from 'react';
import { patientService, consultationService } from '../services/api';
import EditConsultationModal from './EditConsultationModal';
import EditPatientModal from './EditPatientModal';
import AuditTrailPanel from './AuditTrailPanel';
import { CONSULTA_FIELD_LABELS, PACIENTE_FIELD_LABELS } from './fieldLabels';
import './PatientsHistory.css';

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-MX');
};

const formatDateShort = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-MX');
};

const calculateAge = (birthDate) => {
  if (!birthDate) return '-';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const formatCieDiagnostics = (diagnosticos) => {
  if (!Array.isArray(diagnosticos) || diagnosticos.length === 0) {
    return null;
  }

  return diagnosticos
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'object') {
        return { codigo: item.codigo || '', descripcion: item.descripcion || '', categoria: item.categoria || '' };
      }
      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          if (parsed && typeof parsed === 'object') {
            return { codigo: parsed.codigo || '', descripcion: parsed.descripcion || '', categoria: parsed.categoria || '' };
          }
        } catch {
          return { codigo: item, descripcion: '', categoria: '' };
        }
      }
      return { codigo: String(item), descripcion: '', categoria: '' };
    })
    .filter(Boolean);
};

const calculateIMC = (peso, talla) => {
  if (!peso || !talla || Number(talla) === 0) return null;
  return (Number(peso) / (Number(talla) ** 2)).toFixed(1);
};

const truncateText = (value, max = 160) => {
  const text = String(value || '').trim();
  if (!text) return 'No registrado';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
};

const SectionContent = ({ value, label }) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return <p className="no-registrado">No registrado</p>;
  }
  return <p className="section-value">{value}</p>;
};

const AccordionSection = ({ title, defaultOpen = false, children, isEmpty = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`accordion-section ${open ? 'open' : ''} ${isEmpty ? 'empty-section' : ''}`}>
      <button type="button" className="accordion-header" onClick={() => setOpen(!open)}>
        <span className="accordion-title">{title}</span>
        <span className="accordion-icon">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
};

const PatientsHistory = ({ onBack, onResumeConsultation, activeDrafts = [] }) => {
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedConsultas, setExpandedConsultas] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingConsultaId, setEditingConsultaId] = useState(null);
  const [editingPatient, setEditingPatient] = useState(false);
  const [auditRefresh, setAuditRefresh] = useState(0);

  const loadPatients = async (searchTerm = '') => {
    try {
      setLoading(true);
      const result = await patientService.getAll(1, 50, searchTerm);
      setPatients(result.data?.pacientes || []);
    } catch (error) {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (patientId) => {
    try {
      setLoadingHistory(true);
      const result = await patientService.getHistory(patientId);
      setSelectedPatient(result.data?.paciente || null);
      setHistory(result.data?.consultas || []);
      setExpandedConsultas({});
    } catch (error) {
      setSelectedPatient(null);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadPatients(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleConsulta = (id) => {
    setExpandedConsultas(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const all = {};
    filteredHistory.forEach(c => { all[c.id] = true; });
    setExpandedConsultas(all);
  };

  const collapseAll = () => {
    setExpandedConsultas({});
  };

  // Filter patients by gender
  const filteredPatients = genderFilter
    ? patients.filter(p => p.genero === genderFilter)
    : patients;

  const draftCountsByPatient = activeDrafts.reduce((acc, draft) => {
    const patientId = Number(draft.patient_id);
    if (!patientId) return acc;
    acc[patientId] = (acc[patientId] || 0) + 1;
    return acc;
  }, {});

  // Filter consultations by date range
  const filteredHistory = history.filter(c => {
    if (dateFrom && new Date(c.fecha_consulta) < new Date(dateFrom)) return false;
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(c.fecha_consulta) > to) return false;
    }
    return true;
  });

  const handlePrint = () => {
    window.print();
  };

  const handleConsultaSaved = () => {
    setEditingConsultaId(null);
    setAuditRefresh((prev) => prev + 1);
    if (selectedPatient) loadHistory(selectedPatient.id);
  };

  const handlePatientSaved = () => {
    setEditingPatient(false);
    setAuditRefresh((prev) => prev + 1);
    if (selectedPatient) loadHistory(selectedPatient.id);
    loadPatients(search.trim());
  };

  const firstVisit = history.length > 0 ? history[history.length - 1]?.fecha_consulta : null;
  const lastVisit = history.length > 0 ? history[0]?.fecha_consulta : null;

  return (
    <div className="patients-history-container">
      {/* Breadcrumb */}
      <div className="breadcrumb no-print">
        <button type="button" onClick={onBack} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">
          {selectedPatient ? (
            <>
              <button type="button" onClick={() => { setSelectedPatient(null); setHistory([]); }} className="breadcrumb-link">Pacientes</button>
              <span className="breadcrumb-sep">/</span>
              <span>{selectedPatient.nombre}</span>
            </>
          ) : 'Pacientes'}
        </span>
      </div>

      <div className="patients-history-header no-print">
        <h2>Historial de Pacientes</h2>
      </div>

      <div className="patients-history-layout">
        <div className="patients-panel card no-print">
          <h3>Pacientes</h3>

          {activeDrafts.length > 0 && (
            <div className="history-drafts-card">
              <div className="history-drafts-header">
                <span className="history-drafts-title">Borradores activos</span>
                <span className="history-drafts-count">{activeDrafts.length}</span>
              </div>
              <div className="history-drafts-list">
                {activeDrafts.slice(0, 4).map((draft) => (
                  <button
                    type="button"
                    key={`${draft.session_key}-${draft.draft_consultation_id || 'pending'}`}
                    className="history-draft-item"
                    onClick={() => onResumeConsultation && onResumeConsultation(
                      draft.draft_consultation_id || null,
                      draft.session_key || null,
                      'patients-history'
                    )}
                    aria-label={`Continuar borrador de ${draft.patient_name}`}
                  >
                    <div className="history-draft-main">
                      <span className="history-draft-name">{draft.patient_name}</span>
                      <span className="history-draft-step">Paso {draft.current_step || 1}</span>
                    </div>
                    <small className="history-draft-motivo">
                      {draft.motivo_consulta
                        ? truncateText(draft.motivo_consulta, 70)
                        : 'Sin motivo capturado todavia'}
                    </small>
                  </button>
                ))}
              </div>
            </div>
          )}

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o domicilio..."
          />
          <div className="filter-row">
            <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} className="gender-filter">
              <option value="">Todos los géneros</option>
              <option value="Masculino">Masculino</option>
              <option value="Femenino">Femenino</option>
            </select>
          </div>

          {loading ? (
            <div className="panel-status-wrapper">
              <div className="spinner-small"></div>
              <p className="panel-status">Cargando pacientes...</p>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">&#128203;</div>
              <p>No se encontraron pacientes</p>
              {search && <small>Intenta con otro término de búsqueda</small>}
            </div>
          ) : (
            <div className="patients-list">
              {filteredPatients.map((patient) => {
                const draftCount = draftCountsByPatient[patient.id] || 0;

                return (
                <button
                  type="button"
                  key={patient.id}
                  className={`patient-item ${selectedPatient?.id === patient.id ? 'active' : ''}`}
                  onClick={() => loadHistory(patient.id)}
                >
                  <div className="patient-item-top">
                    <span className="patient-name">{patient.nombre}</span>
                    <div className="patient-item-badges">
                      {draftCount > 0 && (
                        <span className="draft-badge">
                          {draftCount === 1 ? 'Borrador' : `${draftCount} borradores`}
                        </span>
                      )}
                      {patient.es_reingreso && <span className="reentry-badge">Reingreso</span>}
                    </div>
                  </div>
                  <small>
                    {patient.genero} · Consultas: {patient.total_consultas || 0}
                    {patient.ultima_consulta ? ` · ${new Date(patient.ultima_consulta).toLocaleDateString('es-MX')}` : ''}
                  </small>
                </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="history-panel card">
          {!selectedPatient && (
            <div className="empty-state large">
              <div className="empty-icon">&#128100;</div>
              <p>Selecciona un paciente para ver su expediente completo</p>
            </div>
          )}

          {selectedPatient && (
            <>
              {/* Patient Summary Card */}
              <div className="patient-summary-card">
                <div className="summary-header">
                  <div className="summary-avatar">
                    {selectedPatient.genero === 'Femenino' ? '\u2640' : '\u2642'}
                  </div>
                  <div className="summary-info">
                    <h3>{selectedPatient.nombre}</h3>
                    <p className="summary-meta">
                      {selectedPatient.genero} · {calculateAge(selectedPatient.fecha_nacimiento)} años
                      · Nacimiento: {formatDateShort(selectedPatient.fecha_nacimiento)}
                    </p>
                  </div>
                  <div className="summary-actions no-print">
                    <button type="button" className="btn btn-secondary" onClick={() => setEditingPatient(true)}>
                      Editar Datos
                    </button>
                    <button type="button" className="btn btn-primary btn-print" onClick={handlePrint}>
                      Imprimir Expediente
                    </button>
                  </div>
                </div>
                <div className="summary-details">
                  <div className="summary-detail-item">
                    <span className="detail-label">Ocupación</span>
                    <span className="detail-value">{selectedPatient.ocupacion || 'No registrada'}</span>
                  </div>
                  <div className="summary-detail-item">
                    <span className="detail-label">Domicilio</span>
                    <span className="detail-value">{selectedPatient.domicilio || 'No registrado'}</span>
                  </div>
                  <div className="summary-detail-item">
                    <span className="detail-label">Tutor</span>
                    <span className="detail-value">{selectedPatient.nombre_tutor || 'N/A'}</span>
                  </div>
                  <div className="summary-detail-item">
                    <span className="detail-label">Total Consultas</span>
                    <span className="detail-value detail-highlight">{history.length}</span>
                  </div>
                  <div className="summary-detail-item">
                    <span className="detail-label">Primera Visita</span>
                    <span className="detail-value">{formatDateShort(firstVisit)}</span>
                  </div>
                  <div className="summary-detail-item">
                    <span className="detail-label">Última Visita</span>
                    <span className="detail-value">{formatDateShort(lastVisit)}</span>
                  </div>
                </div>
                <div className="no-print">
                  <AccordionSection title="Historial de Cambios del Paciente">
                    <AuditTrailPanel
                      fetchTrail={() => patientService.getAuditTrail(selectedPatient.id)}
                      fieldLabels={PACIENTE_FIELD_LABELS}
                      refreshKey={`${selectedPatient.id}-${auditRefresh}`}
                    />
                  </AccordionSection>
                </div>
              </div>

              {/* Date range filter */}
              <div className="history-filters no-print">
                <div className="filter-group">
                  <label>Desde:</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>Hasta:</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                {(dateFrom || dateTo) && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>
                    Limpiar filtro
                  </button>
                )}
                <div className="filter-spacer"></div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={expandAll}>Expandir todo</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={collapseAll}>Colapsar todo</button>
              </div>

              {loadingHistory ? (
                <div className="panel-status-wrapper">
                  <div className="spinner-small"></div>
                  <p className="panel-status">Cargando historial...</p>
                </div>
              ) : (
                <div className="consultas-history-list">
                  {filteredHistory.length === 0 && (
                    <div className="empty-state">
                      <p>{history.length === 0 ? 'Este paciente no tiene consultas registradas.' : 'No hay consultas en el rango seleccionado.'}</p>
                    </div>
                  )}

                  {filteredHistory.map((consulta) => {
                    const isExpanded = expandedConsultas[consulta.id];
                    const imc = calculateIMC(consulta.peso, consulta.talla);
                    const diagnostics = formatCieDiagnostics(consulta.diagnostico_cie10);
                    const diagnosticsCompact = diagnostics && diagnostics.length > 0
                      ? diagnostics.map((d) => d.codigo).filter(Boolean).join(', ')
                      : 'No registrado';

                    return (
                      <div key={consulta.id} className={`consulta-card ${isExpanded ? 'expanded' : ''}`}>
                        <button type="button" className="consulta-card-head" onClick={() => toggleConsulta(consulta.id)}>
                          <div className="consulta-head-left">
                            <h4>Consulta #{consulta.id}</h4>
                            <span className="consulta-date">{formatDate(consulta.fecha_consulta)}</span>
                          </div>
                          <div className="consulta-head-right">
                            <span className="consulta-doctor">Dr. {consulta.doctor_nombre}</span>
                            <span className="expand-icon">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                          </div>
                        </button>

                        {/* Always visible summary */}
                        <div className="consulta-summary">
                          <p><strong>Estado:</strong> {consulta.estado === 'borrador' ? 'Borrador' : 'Completada'}</p>
                          <p><strong>Motivo:</strong> {consulta.motivo_consulta || 'No registrado'}</p>
                          <p><strong>DX:</strong> {diagnosticsCompact}</p>
                          <p><strong>Tratamiento:</strong> {truncateText(consulta.tratamiento)}</p>
                          <p><strong>Solicitud de laboratorio:</strong> {consulta.solicitar_laboratorio ? truncateText(consulta.solicitud_laboratorio || 'Sí', 140) : 'No'}</p>
                          <p><strong>Laboratorio:</strong> {truncateText(consulta.resultados_laboratorio, 140)}</p>
                          <p><strong>Pronóstico:</strong> {truncateText(consulta.pronostico, 140)}</p>
                        </div>

                        {/* Expanded detail sections */}
                        {isExpanded && (
                          <div className="consulta-detail">
                            {consulta.estado === 'borrador' && onResumeConsultation && (
                              <div className="detail-subsection no-print">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={() => onResumeConsultation(consulta.id)}
                                >
                                  Continuar borrador
                                </button>
                              </div>
                            )}

                            {consulta.estado !== 'borrador' && (
                              <div className="detail-subsection no-print">
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setEditingConsultaId(consulta.id)}
                                >
                                  Editar Consulta
                                </button>
                              </div>
                            )}

                            {/* Signos Vitales */}
                            <AccordionSection title="Signos Vitales" defaultOpen={false}>
                              <div className="vitals-grid">
                                <div className="vital-item">
                                  <span className="vital-label">Peso</span>
                                  <span className="vital-value">{consulta.peso ? `${consulta.peso} kg` : '-'}</span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">Talla</span>
                                  <span className="vital-value">{consulta.talla ? `${consulta.talla} m` : '-'}</span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">IMC</span>
                                  <span className="vital-value">{imc ? `${imc} kg/m²` : '-'}</span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">Temperatura</span>
                                  <span className="vital-value">{consulta.temperatura ? `${consulta.temperatura} °C` : '-'}</span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">T/A</span>
                                  <span className="vital-value">
                                    {consulta.tension_arterial_sistolica && consulta.tension_arterial_diastolica
                                      ? `${consulta.tension_arterial_sistolica}/${consulta.tension_arterial_diastolica} mmHg`
                                      : '-'}
                                  </span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">FC</span>
                                  <span className="vital-value">{consulta.frecuencia_cardiaca ? `${consulta.frecuencia_cardiaca} lpm` : '-'}</span>
                                </div>
                                <div className="vital-item">
                                  <span className="vital-label">FR</span>
                                  <span className="vital-value">{consulta.frecuencia_respiratoria ? `${consulta.frecuencia_respiratoria} rpm` : '-'}</span>
                                </div>
                              </div>
                            </AccordionSection>

                            {/* Antecedentes */}
                            <AccordionSection title="Antecedentes"
                              isEmpty={!consulta.antecedentes_familiares && !consulta.antecedentes_personales_patologicos &&
                                       !consulta.antecedentes_personales_no_patologicos && !consulta.antecedentes_gineco_obstetricos}>
                              <div className="detail-subsection">
                                <h5>Antecedentes Familiares</h5>
                                <SectionContent value={consulta.antecedentes_familiares} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Antecedentes Personales Patológicos</h5>
                                <SectionContent value={consulta.antecedentes_personales_patologicos} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Antecedentes Personales No Patológicos</h5>
                                <SectionContent value={consulta.antecedentes_personales_no_patologicos} />
                              </div>
                              {consulta.antecedentes_gineco_obstetricos && (
                                <div className="detail-subsection">
                                  <h5>Antecedentes Gineco-Obstétricos</h5>
                                  <SectionContent value={consulta.antecedentes_gineco_obstetricos} />
                                </div>
                              )}
                            </AccordionSection>

                            {/* Historial Médico */}
                            <AccordionSection title="Historial Médico"
                              isEmpty={!consulta.fracturas && !consulta.enfermedades_previas &&
                                       !consulta.hospitalizaciones_previas && !consulta.fumas_o_tomas && !consulta.adicciones}>
                              <div className="detail-subsection">
                                <h5>Fracturas Previas</h5>
                                <SectionContent value={consulta.fracturas} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Enfermedades Previas</h5>
                                <SectionContent value={consulta.enfermedades_previas} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Hospitalizaciones Previas</h5>
                                <SectionContent value={consulta.hospitalizaciones_previas} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Consumo de Sustancias</h5>
                                <SectionContent value={consulta.fumas_o_tomas} />
                              </div>
                              {consulta.adicciones && (
                                <div className="detail-subsection">
                                  <h5>Adicciones</h5>
                                  <SectionContent value={consulta.adicciones} />
                                </div>
                              )}
                            </AccordionSection>

                            {/* Motivo + Interrogatorio */}
                            <AccordionSection title="Motivo de Consulta e Interrogatorio" defaultOpen={false}>
                              <div className="detail-subsection">
                                <h5>Motivo de Consulta</h5>
                                <SectionContent value={consulta.motivo_consulta} />
                              </div>
                              <div className="detail-subsection">
                                <h5>Interrogatorio por Aparatos y Sistemas</h5>
                                <SectionContent value={consulta.interrogatorio} />
                              </div>
                            </AccordionSection>

                            {/* Exploración Física */}
                            <AccordionSection title="Exploración Física" defaultOpen={false}>
                              <SectionContent value={consulta.exploracion_fisica} />
                            </AccordionSection>

                            {/* Diagnóstico CIE-10 */}
                            <AccordionSection title="Diagnóstico CIE-10" defaultOpen={false}>
                              {diagnostics && diagnostics.length > 0 ? (
                                <div className="diagnostics-list">
                                  {diagnostics.map((d, i) => (
                                    <div key={i} className="diagnostic-item">
                                      <span className="diag-code">{d.codigo}</span>
                                      <span className="diag-desc">{d.descripcion}</span>
                                      {d.categoria && <span className="diag-category">{d.categoria}</span>}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="no-registrado">No registrado</p>
                              )}
                            </AccordionSection>

                            {/* Tratamiento */}
                            <AccordionSection title="Tratamiento" defaultOpen={false}>
                              <SectionContent value={consulta.tratamiento} />
                            </AccordionSection>

                            {/* Resultados de Laboratorio */}
                            <AccordionSection title="Resultados de Laboratorio"
                              isEmpty={!consulta.resultados_laboratorio}>
                              <SectionContent value={consulta.resultados_laboratorio} />
                            </AccordionSection>

                            <AccordionSection
                              title="Solicitud de Laboratorio"
                              defaultOpen={false}
                              isEmpty={!consulta.solicitar_laboratorio && !consulta.solicitud_laboratorio}
                            >
                              <p className="section-value">{consulta.solicitar_laboratorio ? 'Sí' : 'No'}</p>
                              {consulta.solicitud_laboratorio && (
                                <SectionContent value={consulta.solicitud_laboratorio} />
                              )}
                            </AccordionSection>

                            {/* Pronóstico */}
                            <AccordionSection title="Pronóstico" defaultOpen={false}>
                              <SectionContent value={consulta.pronostico} />
                            </AccordionSection>

                            {/* Trazabilidad de ediciones */}
                            <div className="no-print">
                              <AccordionSection title="Historial de Cambios" defaultOpen={false}>
                                <AuditTrailPanel
                                  fetchTrail={() => consultationService.getAuditTrail(consulta.id)}
                                  fieldLabels={CONSULTA_FIELD_LABELS}
                                  refreshKey={`${consulta.id}-${auditRefresh}`}
                                />
                              </AccordionSection>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {editingConsultaId && (
        <EditConsultationModal
          consultaId={editingConsultaId}
          onClose={() => setEditingConsultaId(null)}
          onSaved={handleConsultaSaved}
        />
      )}

      {editingPatient && selectedPatient && (
        <EditPatientModal
          patient={selectedPatient}
          onClose={() => setEditingPatient(false)}
          onSaved={handlePatientSaved}
        />
      )}
    </div>
  );
};

export default PatientsHistory;
